// What the caller is told about one channel: every kind whose choices may be narrowed to a channel
// and of which they may be told on this one — its sales, to whoever holds `websites.storefront.edit`
// and to the channel's operator — each channel's value here and where it came from. Writing here is
// « Me prévenir de chaque vente » — a choice for this channel alone, which wins over the caller's
// organization-wide one.

import { api } from '../client'
import type { components } from '../schema'
import type { UpdateNotificationChoices } from './preferences'

export type WebsiteNotificationKindPreference = components['schemas']['WebsiteNotificationKindPreference']
export type WebsiteNotificationPreferences = components['schemas']['WebsiteNotificationPreferencesView']

function preferencesPath(websiteId: string): string {
  return `/websites/${encodeURIComponent(websiteId)}/notification-preferences`
}

/**
 * The caller's notifications on the channel: `source` is `member_scoped` where they chose for this
 * channel. 404 for a channel of no concern to this organization; an empty list where nothing about
 * this channel may reach them.
 */
export function readWebsiteNotificationPreferences(
  websiteId: string,
  signal?: AbortSignal,
): Promise<WebsiteNotificationPreferences> {
  return api.get<WebsiteNotificationPreferences>(preferencesPath(websiteId), undefined, signal)
}

/**
 * Set (`true` tells whatever holds for the organization, `false` silences this channel alone) or
 * clear (`null`, back to the organization-wide value) the caller's choices for this channel; the
 * answer is the view after the change. 400 for a kind that takes no channel scope or a channel not
 * `selectable` yet, 403 for a kind the caller may not be told of on this channel, 409 while the
 * channel's organization has not crossed off the previous platform, 422 for mail turned off on a
 * mandatory kind.
 */
export function updateWebsiteNotificationPreferences(
  websiteId: string,
  body: UpdateNotificationChoices,
): Promise<WebsiteNotificationPreferences> {
  return api.patch<WebsiteNotificationPreferences>(preferencesPath(websiteId), body)
}
